import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import PaginationControls from "../../components/PaginationControls";
import { getPortfolioStatusBadge } from "../constants/portfolioConstants";
import {AdUnit} from "../../components/AdComponent";
export default function PortfolioListSection({ model }) {
  // List/chart states come from a single section model for consistent rendering.
  const {
    activeKey,
    portfolioLoading,
    portfolioError,
    activeList,
    paginatedList,
    pagination,
    onNavigateTicker,
    onHoverEnter,
    onHoverLeave,
    hoverChartData,
    hoverChartOptions,
    displayName,
    showPercentChart,
    isMobile,
  } = model;
  const { currentPage, totalPages, onPrev, onNext } = pagination;

  const [expandedTicker, setExpandedTicker] = useState(null);
  const chartTooltipText = showPercentChart
    ? "분기별 전체 보유량 대비 해당 종목 비중(%)을 표시합니다."
    : "분기별 보유 수량 변화를 표시합니다.";

  // 모바일에서는 펼친 종목이 현재 페이지에서 사라지면 자동으로 접어준다.
  useEffect(() => {
    if (!isMobile) {
      setExpandedTicker(null);
      return;
    }
    if (!expandedTicker) return;
    if (!paginatedList.some((item) => item.ticker === expandedTicker)) {
      setExpandedTicker(null);
    }
  }, [isMobile, expandedTicker, paginatedList]);

  // 모바일 펼침 상태가 있으면 해당 행의 종목명을 차트 제목으로 우선 사용한다.
  const expandedDisplayName = useMemo(() => {
    if (!expandedTicker) return displayName;
    const selected = paginatedList.find((item) => item.ticker === expandedTicker);
    return selected?.name || selected?.ticker || displayName;
  }, [expandedTicker, paginatedList, displayName]);

  // 데스크톱 우측 패널/모바일 인라인 패널에서 공통으로 쓰는 차트 블록.
  const renderChartPanel = (titleText, titleTicker = null) => (
    <div className="p-2 border rounded-3 h-100 portfolio-inline-chart" style={{ minHeight: "220px" }}>
      {hoverChartData ? (
        <>
          <div className="d-flex align-items-center gap-1 mb-2">
            {isMobile && onNavigateTicker && titleTicker ? (
              <button
                type="button"
                className="btn btn-link p-0 fw-semibold portfolio-inline-chart-title-link"
                onClick={() => onNavigateTicker(titleTicker)}
              >
                {titleText} {showPercentChart ? "보유 비중 추이" : "보유 추이"}
              </button>
            ) : (
              <div className="fw-semibold">
                {titleText} {showPercentChart ? "보유 비중 추이" : "보유 추이"}
              </div>
            )}
            <OverlayTrigger placement="top" overlay={<Tooltip>{chartTooltipText}</Tooltip>}>
              <button type="button" className="btn btn-link p-0 text-secondary">
                <i className="ri-question-line"></i>
              </button>
            </OverlayTrigger>
          </div>
          {isMobile && onNavigateTicker && titleTicker ? (
            <div style={{ height: "300px" }} onClick={() => onNavigateTicker(titleTicker)}>
              <Line data={hoverChartData} options={hoverChartOptions} />
            </div>
          ) : (
            <div style={{ height: "300px" }}>
              <Line data={hoverChartData} options={hoverChartOptions} />
            </div>
          )}

        </>
      ) : (
        <div className="text-secondary fs-sm d-flex h-100 align-items-center justify-content-center text-center">
          차트 데이터가 없습니다.
        </div>
      )}
    </div>
  );

  return (
    <div className="portfolio-mobile-swipe-surface">
      <Row className="g-3">
        <Col lg={activeKey === "all" ? "12" : "6"}>
          {portfolioLoading && activeList.length === 0 ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : portfolioError ? (
            <div className="text-center py-4 text-secondary">{portfolioError}</div>
          ) : activeList.length > 0 ? (
            <div className="d-flex flex-column">
              {paginatedList.map((item, index) => (
                <React.Fragment key={`${item.ticker}-${index}`}>
                  <div
                    className="d-flex align-items-center justify-content-between py-2 portfolio-list-row-item"
                    style={{
                      borderBottom: index < paginatedList.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      // 모바일은 클릭 시 행 확장/축소, 데스크톱은 종목 페이지로 이동한다.
                      if (isMobile) {
                        if (expandedTicker === item.ticker) {
                          setExpandedTicker(null);
                          return;
                        }
                        setExpandedTicker(item.ticker);
                        if (activeKey !== "all") {
                          onHoverEnter(item.ticker);
                        }
                        return;
                      }
                      onNavigateTicker(item.ticker);
                    }}
                    onMouseEnter={() => !isMobile && activeKey !== "all" && onHoverEnter(item.ticker)}
                    onMouseLeave={() => !isMobile && activeKey !== "all" && onHoverLeave()}
                  >
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <span className={`badge ${getPortfolioStatusBadge(item.status)} fs-xs`}>{item.status || "-"}</span>
                      </div>
                      <div>
                        <div className="fw-semibold">{item.name || item.ticker}</div>
                        <div className="text-secondary fs-xs">{item.nickname}</div>
                      </div>
                    </div>
                    <div className="portfolio-intro-list-metrics">
                      <div className="portfolio-intro-list-metric-item">
                        <span className="portfolio-intro-list-metric-label">보유비중</span>
                        <span className="portfolio-intro-list-value">{Number(item.percent).toFixed(2)}%</span>
                      </div>
                      <div className="portfolio-intro-list-metric-item">
                        <span className="portfolio-intro-list-metric-label">변화량</span>
                        <span className={`portfolio-intro-list-diff ${Number(item.diff).toFixed(2) >= 0 ? " text-success" : "text-danger"}`}>
                          {Number(item.diff).toFixed(2) >= 0 ? "+" : ""}{Number(item.diff).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {isMobile && activeKey !== "all" && expandedTicker === item.ticker && (
                    // 모바일에서는 선택된 행 바로 아래에 차트를 인라인으로 렌더링한다.
                    <div className="pt-2 pb-3">
                      {renderChartPanel(expandedDisplayName, expandedTicker)}
                    </div>
                  )}
                </React.Fragment>
              ))}
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={onPrev}
                onNext={onNext}
                className="pt-3"
              />
            </div>
          ) : (
            <div className="text-center py-4 text-secondary">데이터가 없습니다</div>
          )}
        </Col>

        {activeKey !== "all" && !isMobile && (
          <Col lg="6">
            {renderChartPanel(displayName)}
          </Col>
        )}
      </Row>
      <div className="d-flex justify-content-center my-4">
        <div style={{ width: '100%', maxWidth: '970px' }}>
          <AdUnit key="chart_3951546463" slot="3951546463" style={{ overflowX: "scroll", overflowY: "hidden" }} />
        </div>
      </div>
    </div>
  );
}
